// src/data/adapters/ticajeOnlineAdapter.js
import { Global } from "../../helpers/Global";

/** Cabeceras con token */
function tokenHeaders(extra = {}) {
  const t = localStorage.getItem("token");
  return {
    ...extra,
    Authorization: t ? `Bearer ${t}` : "",
    "Content-Type": extra["Content-Type"] || "application/json",
  };
}

/** Normaliza la intervención activa del backend -> shape de la UI */
export function normalizeActiveTicajeFromServer(t) {
  if (!t) return null;

  return {
    id: t.id,

    inicio: t.inicio ?? t.fecha_inicio ?? t.created_at ?? null,

    cliente_id: t.cliente_id ?? t.cliente?.id ?? null,
    centro_id:  t.centro_id  ?? t.centro?.id  ?? null,
    orden_id:   t.orden_id   ?? t.orden?.id   ?? null,

    cliente_nombre:
      t.cliente_nombre ??
      t.cliente?.nombre ??
      "",
    centro_nombre:
      t.centro_nombre ??
      t.centro?.nombre ??
      "",
    orden_titulo:
      t.orden_titulo ??
      t.orden?.titulo ??
      "",

    comentarios: t.comentarios ?? null,

    latitud_inicio:  t.latitud ?? t.latitud_inicio ?? null,
    longitud_inicio: t.longitud ?? t.longitud_inicio ?? null,
  };
}

export const TicajeOnlineAdapter = {

  // ---------------------------------------------------------------------------
  // CATÁLOGOS (para IniciarIntervencion)
  // ---------------------------------------------------------------------------

  /** Ordenes de trabajo activas */
  async getOrdenes() {
    const res = await fetch(`${Global.url}ordenes`, {
      headers: tokenHeaders(),
      cache: "no-store",
    });
    if (!res.ok) throw new Error("No se pudieron obtener las órdenes (online)");

    const data = await res.json().catch(() => []);
    const lista = Array.isArray(data) ? data : [];

    // Lógica actual del frontend
    const activas = lista.filter(
      (o) =>
        ["pendiente", "en curso"].includes((o.estado || "").toLowerCase()) &&
        o.activo === true
    );
    return activas;
  },

  /** Clientes */
  async getClientes() {
    const res = await fetch(`${Global.url}clientes`, {
      headers: tokenHeaders(),
      cache: "no-store",
    });
    if (!res.ok) throw new Error("No se pudieron obtener los clientes (online)");
    const data = await res.json().catch(() => []);
    return Array.isArray(data) ? data : [];
  },

  /** Centros asociados a una OT */
  async getCentrosPorOrden(ordenId) {
    const id = Number(ordenId);
    if (!id) return [];
    const res = await fetch(`${Global.url}ordenes/${id}/centros`, {
      headers: tokenHeaders(),
      cache: "no-store",
    });
    if (!res.ok) throw new Error("No se pudieron obtener los centros de la OT (online)");

    const data = await res.json().catch(() => []);
    const centrosData = Array.isArray(data) ? data : data.data || [];
    return centrosData;
  },

  // ---------------------------------------------------------------------------
  // INTERVENCIÓN ACTIVA
  // ---------------------------------------------------------------------------

  /**
   * GET /ticajes/activa
   * 204 → no hay activa
   * 200 → intervención activa
   */
  async getActive() {
    const res = await fetch(
      `${Global.url}ticajes/activa?t=${Date.now()}`,
      { headers: tokenHeaders(), cache: "no-store" }
    );

    if (res.status === 204) return null;
    if (res.status === 404) return null;  // seguridad extra

    if (!res.ok) throw new Error(`Error al leer intervención activa (online): ${res.status}`);

    const data = await res.json();
    return normalizeActiveTicajeFromServer(data);
  },

  // ---------------------------------------------------------------------------
  // START / FINISH
  // ---------------------------------------------------------------------------

  /**
   * Iniciar intervención
   */
  async start(payload, options = {}) {
    const {
      cliente_id,
      centro_id,
      orden_id,
      comentarios,
      latitud,
      longitud,
      fingerprint,
      inicio,
      inicio_client,
    } = payload || {};

    const offlineReplay = !!options.offlineReplay;

    const idem =
      options.idemKey ||
      (crypto?.randomUUID?.() || `idem_${Date.now()}_${Math.random()}`);

    const headersExtra = { "X-Idempotency-Key": idem };
    if (offlineReplay) headersExtra["X-Offline-Replay"] = "1";

    const body = {
      cliente_id,
      centro_id,
      orden_id,
      comentarios,
      fingerprint,
      latitud,
      longitud,
    };

    if (offlineReplay) {
      const ic = inicio_client || inicio;
      if (ic) body.inicio_client = ic;
    } else if (inicio) {
      body.inicio = inicio;
    }

    const res = await fetch(`${Global.url}ticajes`, {
      method: "POST",
      headers: tokenHeaders(headersExtra),
      body: JSON.stringify(body),
    });

    if (res.ok) {
      const data = await res.json().catch(() => ({}));
      return { ok: true, conflict: false, status: res.status, data };
    }

    if (res.status === 409) {
      return { ok: true, conflict: true, status: res.status, data: null };
    }

    throw new Error(`Fallo al iniciar intervención (online): ${res.status}`);
  },

  /**
   * Finalizar intervención actual
   *
   * PUT /ticajes/finalizar/actual
   */
  async finishActual(payload, options = {}) {
    const {
      motivo_fin = "finalizada",
      latitud_cierre,
      longitud_cierre,
      en_rango_cierre,
      distancia_m_cierre,
      fin_client,
    } = payload || {};

    const offlineReplay = !!options.offlineReplay;
    const headersExtra = {};
    if (offlineReplay) headersExtra["X-Offline-Replay"] = "1";

    const body = {
      motivo_fin,
      latitud_cierre,
      longitud_cierre,
      en_rango_cierre,
      distancia_m_cierre,
    };

    if (offlineReplay && fin_client) {
      body.fin_client = fin_client;
    }

    const res = await fetch(`${Global.url}ticajes/finalizar/actual`, {
      method: "PUT",
      headers: tokenHeaders(headersExtra),
      body: JSON.stringify(body),
    });

    if (res.ok) return { ok: true, idempotent: false, status: res.status };

    if (res.status === 404 || res.status === 409) {
      return { ok: true, idempotent: true, status: res.status };
    }

    throw new Error(`Fallo al finalizar intervención (online): ${res.status}`);
  },

  // ---------------------------------------------------------------------------
  // CANCEL (solo por ID)
  // ---------------------------------------------------------------------------

  /**
   * CANCELAR intervención no tiene equivalente "actual" en backend.
   * Solo existe:
   *   PUT /ticajes/cancelar/:id
   *
   * Por lo tanto, no implementamos cancelActual aquí.
   */
};
